// ./src/components/features/Cars/CarBodyTypeSelector/CarBodyTypeSelector.tsx
import React, { useMemo, useCallback } from "react";
import { Select, SelectProps, Group, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CarBodyType } from "@/utils/api";
import { useAppEssentialsContext } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { BODY_TYPE_IDS } from "@/config";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface CarBodyTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected body type ID */
  value?: number | string | null | undefined;

  /** Callback fired when body type selection changes: (name, value, bodyType?) */
  onChange?: (
    name: string,
    value: number | null,
    bodyType?: CarBodyType
  ) => void;

  /** Placeholder text when no body type is selected */
  placeholder?: string;

  /** Whether to include inactive body types in the list */
  includeInactive?: boolean;

  /** Custom filter function to filter body types */
  bodyTypeFilter?: (bodyType: CarBodyType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no body types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show description as hint below the input */
  showDescriptionHint?: boolean;

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface
// ============================================

interface BodyTypeOptionData {
  value: string;
  label: string;
  iconName: string;
  disabled?: boolean;
}

// ============================================
// Constants - Icon mapping for vehicle types
// ============================================

const BODY_TYPE_ICONS: Record<number, string> = {
  [BODY_TYPE_IDS.UNKNOWN]: "Car",
  [BODY_TYPE_IDS.SEDAN]: "Car",
  [BODY_TYPE_IDS.HATCHBACK]: "Car",
  [BODY_TYPE_IDS.SUV]: "Car",
  [BODY_TYPE_IDS.CROSSOVER]: "Car",
  [BODY_TYPE_IDS.WAGON]: "Car",
  [BODY_TYPE_IDS.COUPE]: "Car",
  [BODY_TYPE_IDS.CONVERTIBLE]: "Car",
  [BODY_TYPE_IDS.SPORT]: "Car",
  [BODY_TYPE_IDS.LIMOUSINE]: "Car",
  [BODY_TYPE_IDS.TRUCK]: "Truck",
  [BODY_TYPE_IDS.PICKUP]: "Truck",
  [BODY_TYPE_IDS.MICROVAN]: "Car",
  [BODY_TYPE_IDS.MINIVAN]: "Car",
  [BODY_TYPE_IDS.VAN]: "Truck",
  [BODY_TYPE_IDS.RV]: "Truck",
  [BODY_TYPE_IDS.MINIBUS]: "Bus",
  [BODY_TYPE_IDS.BUS]: "Bus",
  [BODY_TYPE_IDS.FREIGHT]: "Truck",
  [BODY_TYPE_IDS.MOTORCYCLE]: "Motorbike",
  [BODY_TYPE_IDS.SCOOTER]: "Motorbike",
  [BODY_TYPE_IDS.OTHER]: "QuestionMark",
};

const getBodyTypeIcon = (id: number | undefined): string => {
  if (id === undefined) return "Car";
  return BODY_TYPE_ICONS[id] || "Car";
};

// ============================================
// Component
// ============================================

const CarBodyTypeSelectorComponent: React.FC<CarBodyTypeSelectorProps> = ({
  name = "bodyTypeId",
  value,
  onChange,
  placeholder,
  includeInactive = false,
  bodyTypeFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  showDescriptionHint = false,
  label,
  ...selectProps
}) => {
  const { t } = useTranslation(["cars", "common"]);
  const { appEssentials, isLoading, lastError, getCarBodyTypeById } =
    useAppEssentialsContext();

  const bodyTypes = appEssentials?.carBodyTypes || [];

  // Default translations
  const defaultPlaceholder =
    placeholder || t("cars:selectors.bodyType.placeholder");
  const defaultLabel = label ?? t("cars:selectors.bodyType.label");
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("cars:selectors.bodyType.loading");
  const defaultEmptyText = emptyText || t("cars:selectors.bodyType.empty");

  // Create a map for quick body type lookup by ID
  const bodyTypeMap = useMemo(() => {
    const map = new Map<number, CarBodyType>();
    bodyTypes.forEach((bodyType) => {
      if (bodyType.id !== undefined) {
        map.set(bodyType.id, bodyType);
      }
    });
    return map;
  }, [bodyTypes]);

  // Filter and prepare body type options
  const bodyTypeOptions = useMemo((): BodyTypeOptionData[] => {
    let filteredTypes = bodyTypes;

    // Apply custom filter if provided
    if (bodyTypeFilter) {
      filteredTypes = filteredTypes.filter(bodyTypeFilter);
    }

    // Convert to select options
    return filteredTypes.map((bodyType) => ({
      value: String(bodyType.id),
      label: bodyType.name || "Unknown",
      iconName: getBodyTypeIcon(bodyType.id),
    }));
  }, [bodyTypes, bodyTypeFilter]);

  // Get selected body type for icon display
  const selectedBodyType = useMemo(() => {
    if (value === null || value === undefined) return null;
    const numericValue =
      typeof value === "string" ? parseInt(value, 10) : value;
    return bodyTypeMap.get(numericValue) || getCarBodyTypeById(numericValue);
  }, [value, bodyTypeMap, getCarBodyTypeById]);

  // Handle onChange with body type object lookup
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue) {
        const numericId = parseInt(selectedValue, 10);
        const bodyType =
          bodyTypeMap.get(numericId) || getCarBodyTypeById(numericId);
        onChange?.(name, numericId, bodyType);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, bodyTypeMap, getCarBodyTypeById, onChange]
  );

  // Render option for dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as BodyTypeOptionData;
      return (
        <Group gap="sm" wrap="nowrap">
          <AppIcon iconName={optionData.iconName} size={20} />
          <Text size="md" fw={500}>
            {optionData.label}
          </Text>
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    if (!selectedBodyType) return undefined;
    return (
      <AppIcon iconName={getBodyTypeIcon(selectedBodyType.id)} size={18} />
    );
  }, [selectedBodyType]);

  // Determine current state
  const isEmpty = !isLoading && bodyTypes.length === 0;

  // Memoize select state
  const selectState = useMemo(() => {
    if (isLoading && bodyTypes.length === 0) {
      return {
        data: [] as BodyTypeOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !isLoading && bodyTypes.length === 0) {
      return {
        data: [] as BodyTypeOptionData[],
        placeholder: t("common:errors.generic"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError"),
      };
    }

    if (isEmpty) {
      return {
        data: [] as BodyTypeOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: bodyTypeOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || isLoading,
      showLoading: isLoading && bodyTypes.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    isLoading,
    bodyTypes.length,
    lastError,
    isEmpty,
    bodyTypeOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="gray" />;
    }
    return selectProps.rightSection;
  }, [selectState.showLoading, size, selectProps.rightSection]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={value !== null && value !== undefined ? String(value) : null}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        required={required}
        allowDeselect={!renderOption}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
      />
    </Box>
  );
};

export const CarBodyTypeSelector = React.memo(CarBodyTypeSelectorComponent);
